// src/components/Finance/StaffLoans.jsx
//
// Finance -> Staff Loans.
// * Employees are loaded from the existing Employee Management records (active
//   employees only) through /staff-loans/form-options - nothing is duplicated.
// * The database is the only source of truth: every balance, status and
//   repayment shown here is read back from the API (GET /staff-loans) after
//   each change. Both tabs below are built from that one response - nothing
//   is fetched twice and nothing is computed or stored outside what the
//   backend already returns.
// * Receive Cash posts the repayment AND the matching Cash Book entry in one
//   backend transaction (see server/controllers/staffLoanController.js).
//
// Two tabs:
//   1) Staff Loan Ledger - a read-only financial ledger (Date, Employee Name,
//      Description, Debit, Credit, Balance), laid out exactly like the other
//      Finance ledgers (Salary Advance / Salary Deduction / Other Ledgers):
//      every loan issue and every repayment (Receive Cash or a Salary Entry
//      deduction) is one row, oldest first, with one running balance. No
//      per-row actions here, same as the sibling ledger pages.
//   2) Staff Loan Details - an employee-wise (and, since loans are
//      company-specific, employee+company-wise) rollup of the SAME loans:
//      Total Loan Amount / Total Amount Deducted / Remaining Balance come
//      straight from each loan's own `loan_amount` / `balance` (never
//      recomputed independently), Monthly Deduction Amount and Last
//      Deduction Date come from the latest Salary Entry repayment(s) on
//      those loans. "Create Staff Loan" and "Receive Cash" / history stay
//      here, via the existing Current Loans popup, unchanged.
//
// Duplicate salary deductions are already prevented server-side (see
// server/controllers/salaryEntryController.js applySalaryEntryStaffLoanRepayment -
// re-saving the same month replaces, never re-adds, the repayment row), so
// both tabs below simply read the already-deduplicated repayment history.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandHoldingDollar, faSpinner, faCheck, faHistory, faTimes, faTable, faPlus, faBookOpen, faUsers } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';
import useDialog from '../common/ui/useDialog';
import { usePopup } from '../context/PopupContext';
import './FinanceForm.css';
import './FinanceLedger.css';
import './StaffLoans.css';

const WRITE_ROLES = ['Admin', 'Super Admin', 'Accountant'];

const getStoredUserRole = () => {
  try {
    const storedUser = localStorage.getItem('user');
    const role = storedUser ? JSON.parse(storedUser).role : null;
    return role === 'QB' ? 'View Only' : role;
  } catch (e) {
    return null;
  }
};

// Local (not UTC) calendar date, so the default is correct early in the morning in Sri Lanka.
const todayLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const fmtMoney = (value) =>
  (Number.parseFloat(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

// 'YYYY-MM-DD' -> 'DD/MM/YYYY' without going through Date (no timezone shifts).
const fmtDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
};

const isRealDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 2000 || year > 2100) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
};

// Mirrors the backend rule (utils/staffLoanUtils.js parsePositiveMoney): positive,
// at most 2 decimal places. Returns { cents } or { error }.
const parseMoney = (raw, label) => {
  const text = String(raw ?? '').trim();
  if (!text) return { error: `${label} is required` };
  if (text.startsWith('-')) return { error: `${label} must be greater than zero` };
  if (!/^(\d*)(\.\d*)?$/.test(text) || text === '.') return { error: `${label} must be a valid number` };

  const [integerPart, fractionPart = ''] = text.split('.');
  if (fractionPart.length > 2) return { error: `${label} can have at most 2 decimal places` };
  if (integerPart.replace(/^0+(?=\d)/, '').length > 12) return { error: `${label} is too large` };

  const cents = Number(integerPart || '0') * 100 + Number(fractionPart.padEnd(2, '0'));
  if (cents <= 0) return { error: `${label} must be greater than zero` };
  return { cents };
};

const emptyForm = (companies = []) => ({
  employee_id: '',
  company_id: companies.length === 1 ? String(companies[0].id) : '',
  loan_amount: '',
  issued_date: todayLocal(),
  cheque_details: ''
});

// Amount shown as a Debit, a Credit, or '-' when zero - identical to the
// money() helper the other Finance ledgers (Salary Advance, Salary Deduction,
// Other Ledgers) use, so Debit/Credit/Balance line up the same way everywhere.
const money = (value, cls) => (
  (Number.parseFloat(value) || 0) > 0
    ? <span className={cls}>{fmtMoney(value)}</span>
    : <span className="fl-empty-val">—</span>
);

const StaffLoans = () => {
  const { showPopup } = usePopup();
  const canWrite = WRITE_ROLES.includes(getStoredUserRole());

  const [activeTab, setActiveTab] = useState('ledger'); // 'ledger' | 'details'

  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [options, setOptions] = useState({ employees: [], companies: [] });

  // Company filter, shared by both tabs (same '/companies' list other Salary
  // pages use) - independent of the write-only form-options below, and never
  // touches the Create Staff Loan form's own Company field.
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [filters, setFilters] = useState({ companyId: '', dateFrom: '', dateTo: '', search: '' });

  // Adjustments -> Staff Loans Opening Balance (one per company, set from the
  // Adjustments page - see client/src/components/adjustments/
  // StaffLoanOpeningBalanceAdjustment.jsx). Merged into the Ledger below as a
  // standalone opening transaction; never attached to an employee, so the
  // Staff Loan Details tab never reads this.
  const [openingBalances, setOpeningBalances] = useState([]);

  // Staff Loan Details tab: employee-wise search, on top of the shared Company filter.
  const [detailsSearch, setDetailsSearch] = useState('');

  // Create Staff Loan form (a popup, opened from the shared header)
  const [form, setForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Receive Cash modal
  const [receiveLoan, setReceiveLoan] = useState(null);
  const [receiveForm, setReceiveForm] = useState({ amount: '', date: '' });
  const [receiveError, setReceiveError] = useState('');
  const [receiving, setReceiving] = useState(false);

  // Repayment history modal
  const [historyLoan, setHistoryLoan] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Current Loans popup, scoped to one employee (opened from that employee's
  // row on the Staff Loan Details tab) - { employee_id, employee_name } or null.
  const [loansModalEmployee, setLoansModalEmployee] = useState(null);

  // Escape, focus, Tab trap and scroll lock for every popup (handlers are declared below).
  const createDialogRef = useRef(null);
  const receiveDialogRef = useRef(null);
  const historyDialogRef = useRef(null);
  const loansDialogRef = useRef(null);
  const closeCreateModal = () => { if (!creating) setShowCreateModal(false); };
  useDialog(createDialogRef, { open: showCreateModal, onClose: closeCreateModal });
  useDialog(receiveDialogRef, { open: Boolean(receiveLoan), onClose: () => closeReceiveModal() });
  useDialog(historyDialogRef, { open: Boolean(historyLoan), onClose: () => setHistoryLoan(null) });
  useDialog(loansDialogRef, { open: Boolean(loansModalEmployee), onClose: () => setLoansModalEmployee(null) });

  const fetchLoans = useCallback(async () => {
    try {
      const response = await api.get('/staff-loans');
      setLoans(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showPopup('error', error.response?.data?.error || 'Failed to load staff loans');
    } finally {
      setLoadingLoans(false);
    }
  }, [showPopup]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  useEffect(() => {
    api.get('/staff-loans/opening-balances')
      .then((response) => setOpeningBalances(Array.isArray(response.data) ? response.data : []))
      .catch(() => setOpeningBalances([]));
  }, []);

  // Company filter options, available to every role (same '/companies' list
  // other Salary pages use), independent of the write-only form-options below.
  useEffect(() => {
    api.get('/companies')
      .then((response) => {
        const list = Array.isArray(response.data) ? response.data : [];
        setFilterCompanies(list);
        setFilters((prev) => (prev.companyId || list.length === 0 ? prev : { ...prev, companyId: String(list[0].id) }));
      })
      .catch(() => setFilterCompanies([]));
  }, []);

  useEffect(() => {
    if (!canWrite) return;
    api.get('/staff-loans/form-options')
      .then((response) => {
        const data = response.data || {};
        const nextOptions = {
          employees: Array.isArray(data.employees) ? data.employees : [],
          companies: Array.isArray(data.companies) ? data.companies : []
        };
        setOptions(nextOptions);
        setForm((prev) => (
          prev.company_id || nextOptions.companies.length !== 1
            ? prev
            : { ...prev, company_id: String(nextOptions.companies[0].id) }
        ));
      })
      .catch((error) => {
        showPopup('error', error.response?.data?.error || 'Failed to load employees');
      });
  }, [canWrite, showPopup]);

  // ─── Create Staff Loan ────────────────────────────────────────────────────
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.employee_id) errors.employee_id = 'Please select an employee';
    if (!form.company_id) errors.company_id = 'Please select a company';

    const amount = parseMoney(form.loan_amount, 'Loan Amount');
    if (amount.error) errors.loan_amount = amount.error;

    if (!form.issued_date) errors.issued_date = 'Issued Date is required';
    else if (!isRealDate(form.issued_date)) errors.issued_date = 'Please enter a valid date';

    if (form.cheque_details.trim().length > 500) {
      errors.cheque_details = 'Cheque details must be 500 characters or fewer';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateLoan = async (event) => {
    event.preventDefault();
    if (creating || !validateForm()) return;

    setCreating(true);
    try {
      await api.post('/staff-loans', {
        employee_id: Number(form.employee_id),
        company_id: Number(form.company_id),
        loan_amount: form.loan_amount.trim(),
        issued_date: form.issued_date,
        cheque_details: form.cheque_details.trim()
      });
      showPopup('success', 'Staff loan created successfully');
      // Keep the chosen company (one accountant usually works with one company) and clear the rest.
      setForm({ ...emptyForm(options.companies), company_id: form.company_id });
      setFormErrors({});
      setShowCreateModal(false);
      await fetchLoans();
    } catch (error) {
      showPopup('error', error.response?.data?.error || 'Failed to create staff loan');
    } finally {
      setCreating(false);
    }
  };

  // ─── Receive Cash ─────────────────────────────────────────────────────────
  const openReceiveModal = (loan) => {
    setReceiveLoan(loan);
    setReceiveForm({ amount: '', date: todayLocal() });
    setReceiveError('');
  };

  const closeReceiveModal = () => {
    if (receiving) return;
    setReceiveLoan(null);
    setReceiveError('');
  };

  const handleReceiveCash = async (event) => {
    event.preventDefault();
    if (receiving || !receiveLoan) return;

    const amount = parseMoney(receiveForm.amount, 'Amount');
    if (amount.error) return setReceiveError(amount.error);
    if (amount.cents > Math.round(receiveLoan.balance * 100)) {
      return setReceiveError(`Amount cannot exceed the outstanding balance of Rs. ${fmtMoney(receiveLoan.balance)}`);
    }
    if (!receiveForm.date) return setReceiveError('Date is required');
    if (!isRealDate(receiveForm.date)) return setReceiveError('Please enter a valid date');
    if (receiveForm.date < receiveLoan.issued_date) {
      return setReceiveError('Date cannot be earlier than the date the loan was issued');
    }

    setReceiving(true);
    setReceiveError('');
    try {
      const response = await api.post(`/staff-loans/${receiveLoan.id}/repayments`, {
        amount: receiveForm.amount.trim(),
        received_date: receiveForm.date
      });
      const updated = response.data?.loan;
      const settledNote = updated?.status === 'Settled' ? ' The loan is now fully settled.' : '';
      showPopup(
        'success',
        `Received Rs. ${fmtMoney(amount.cents / 100)} from ${receiveLoan.employee_name}. Loan balance is now Rs. ${fmtMoney(updated?.balance)}.${settledNote}`
      );
      setReceiveLoan(null);
      await fetchLoans();
    } catch (error) {
      setReceiveError(error.response?.data?.error || 'Failed to receive cash');
      // The loan may have changed since this page loaded (e.g. settled by another user).
      fetchLoans();
    } finally {
      setReceiving(false);
    }
  };

  // ─── Repayment history ────────────────────────────────────────────────────
  const openHistory = async (loan) => {
    setHistoryLoan(loan);
    setHistory([]);
    setLoadingHistory(true);
    try {
      const response = await api.get(`/staff-loans/${loan.id}/repayments`);
      setHistory(response.data?.repayments || []);
    } catch (error) {
      showPopup('error', error.response?.data?.error || 'Failed to load repayment history');
      setHistoryLoan(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Loans narrowed to the shared Company filter - both tabs below start from this.
  const companyScopedLoans = useMemo(
    () => loans.filter((loan) => !filters.companyId || String(loan.company_id) === filters.companyId),
    [loans, filters.companyId]
  );

  // ════════════════════════════════════════════════════════════════════════
  // Staff Loan Ledger - every loan issue and every repayment, flattened into
  // one chronological, consolidated list with a single running balance,
  // exactly like Salary Advance / Salary Deduction / Other Ledgers:
  //   Debit  = a loan issued (money the company expects back goes up)
  //   Credit = a repayment received, whether Receive Cash or Salary Entry
  //            deductions (money owed goes down).
  // Every employee's Salary Entry deduction (and any reversal from editing
  // that entry) for the same company + salary month is summed into ONE
  // "Total Salary Entry Deduction (YYYY-MM)" row - recomputed fresh from the
  // live repayment rows every time, so an edited/re-saved Salary Entry is
  // reflected without ever posting a second ledger row for that month.
  // Individual employee amounts stay intact in Staff Loan Details (built
  // separately below, straight from the same loans/repayments).
  // ════════════════════════════════════════════════════════════════════════
  const openingBalanceForCompany = openingBalances.find((balance) => String(balance.company_id) === filters.companyId) || null;

  const ledgerData = useMemo(() => {
    const transactions = [];
    let seq = 0;

    // Adjustments -> Staff Loans Opening Balance (if any) for the selected
    // company - not tied to any loan or employee, so it is pushed once,
    // ahead of every real transaction (its date is enforced to be earlier
    // than all of them when it is saved).
    if (openingBalanceForCompany) {
      const amount = Number(openingBalanceForCompany.amount) || 0;
      transactions.push({
        key: `opening-balance-${openingBalanceForCompany.company_id}`,
        seq: seq++,
        date: openingBalanceForCompany.date,
        employee_name: '—',
        description: openingBalanceForCompany.remark ? `Opening Balance — ${openingBalanceForCompany.remark}` : 'Opening Balance',
        debit: amount > 0 ? amount : 0,
        credit: amount < 0 ? Math.abs(amount) : 0
      });
    }

    // salary_month ('YYYY-MM') -> { date, cents }. Reversal rows (negative
    // amount, posted when a Salary Entry is edited) net out against the
    // original here, so the total always matches what is currently saved.
    const salaryEntryTotalsByMonth = new Map();

    [...companyScopedLoans]
      .sort((a, b) => String(a.issued_date).localeCompare(String(b.issued_date)) || a.id - b.id)
      .forEach((loan) => {
        transactions.push({
          key: `loan-${loan.id}`,
          seq: seq++,
          date: loan.issued_date,
          employee_name: loan.employee_name,
          description: loan.cheque_details ? `Staff Loan Issued — ${loan.cheque_details}` : 'Staff Loan Issued',
          debit: Number(loan.loan_amount) || 0,
          credit: 0
        });

        (loan.repayments || []).forEach((repayment) => {
          const amount = Number(repayment.amount) || 0;

          if (repayment.source_type === 'SALARY_ENTRY' || repayment.source_type === 'SALARY_ENTRY_REVERSAL') {
            const month = repayment.salary_month || String(repayment.received_date || '').slice(0, 7);
            const existing = salaryEntryTotalsByMonth.get(month);
            const cents = Math.round(amount * 100);
            if (!existing) {
              salaryEntryTotalsByMonth.set(month, { date: repayment.received_date, cents });
            } else {
              existing.cents += cents;
              if (String(repayment.received_date) < String(existing.date)) existing.date = repayment.received_date;
            }
            return;
          }

          // Receive Cash - unchanged, still its own row.
          transactions.push({
            key: `repayment-${repayment.id}`,
            seq: seq++,
            date: repayment.received_date,
            employee_name: loan.employee_name,
            description: 'Loan Repayment Received',
            debit: amount < 0 ? Math.abs(amount) : 0,
            credit: amount < 0 ? 0 : amount
          });
        });
      });

    // One consolidated row per salary month, across every employee/loan.
    salaryEntryTotalsByMonth.forEach(({ date, cents }, month) => {
      if (cents === 0) return; // fully reversed with nothing re-applied - nothing to show
      const amount = cents / 100;
      const isReversal = amount < 0;
      transactions.push({
        key: `salary-entry-total-${month}`,
        seq: seq++,
        date,
        employee_name: '—',
        description: `Total Salary Entry Deduction (${month})`,
        debit: isReversal ? Math.abs(amount) : 0,
        credit: isReversal ? 0 : amount
      });
    });

    transactions.sort((a, b) => String(a.date).localeCompare(String(b.date)) || a.seq - b.seq);

    let running = 0;
    transactions.forEach((transaction) => {
      running += transaction.debit - transaction.credit;
      transaction.balance_after = running;
    });

    let openingBalance = 0;
    const visible = [];
    transactions.forEach((transaction) => {
      if (filters.dateFrom && String(transaction.date) < filters.dateFrom) {
        openingBalance = transaction.balance_after;
        return;
      }
      if (filters.dateTo && String(transaction.date) > filters.dateTo) return;
      visible.push(transaction);
    });

    return { transactions: visible, openingBalance };
  }, [companyScopedLoans, filters.dateFrom, filters.dateTo, openingBalanceForCompany]);

  // Search narrows which already-filtered (by Company/date) rows are shown
  // and totalled - same effect as Cash Ledger's Reference search field.
  const visibleLedgerTransactions = ledgerData.transactions.filter((transaction) => {
    if (!filters.search.trim()) return true;
    const term = filters.search.trim().toLowerCase();
    return transaction.employee_name.toLowerCase().includes(term) || transaction.description.toLowerCase().includes(term);
  });

  const ledgerTotals = visibleLedgerTransactions.reduce((acc, transaction) => ({
    debit: acc.debit + transaction.debit,
    credit: acc.credit + transaction.credit
  }), { debit: 0, credit: 0 });

  // Current outstanding balance is always "as of now" - never narrowed by the
  // date filter - and includes the company's Opening Balance adjustment, if any.
  const currentBalance = companyScopedLoans.reduce((sum, loan) => sum + (Number(loan.balance) || 0), 0)
    + (Number(openingBalanceForCompany?.amount) || 0);

  // ════════════════════════════════════════════════════════════════════════
  // Staff Loan Details - one row per employee (per company, since every loan
  // belongs to exactly one company). Totals are summed straight from each
  // loan's own loan_amount/balance, so an employee with several loans always
  // shows one correct, consolidated row.
  // ════════════════════════════════════════════════════════════════════════
  const loanDetailGroups = useMemo(() => {
    const groups = new Map();

    loans.forEach((loan) => {
      const key = `${loan.employee_id}-${loan.company_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          employee_id: loan.employee_id,
          employee_name: loan.employee_name,
          company_id: loan.company_id,
          company_name: loan.company_name,
          totalLoanAmount: 0,
          totalDeducted: 0,
          remainingBalance: 0,
          lastDeductionDate: null,
          latestSalaryMonth: null,
          monthlyDeductionAmount: 0
        });
      }

      const group = groups.get(key);
      const loanAmount = Number(loan.loan_amount) || 0;
      const balance = Number(loan.balance) || 0;
      group.totalLoanAmount += loanAmount;
      group.remainingBalance += balance;
      group.totalDeducted += loanAmount - balance; // = net total received (reversal-adjusted, from the loan's own balance)

      (loan.repayments || []).forEach((repayment) => {
        const amount = Number(repayment.amount) || 0;
        if (amount > 0 && (!group.lastDeductionDate || repayment.received_date > group.lastDeductionDate)) {
          group.lastDeductionDate = repayment.received_date;
        }
        if (repayment.source_type === 'SALARY_ENTRY' && repayment.salary_month) {
          if (!group.latestSalaryMonth || repayment.salary_month > group.latestSalaryMonth) {
            group.latestSalaryMonth = repayment.salary_month;
            group.monthlyDeductionAmount = amount;
          } else if (repayment.salary_month === group.latestSalaryMonth) {
            group.monthlyDeductionAmount += amount;
          }
        }
      });
    });

    return [...groups.values()]
      .map((group) => ({ ...group, status: group.remainingBalance > 0 ? 'Active' : 'Settled' }))
      .sort((a, b) => a.employee_name.localeCompare(b.employee_name));
  }, [loans]);

  const visibleLoanDetailGroups = loanDetailGroups.filter((group) => {
    if (filters.companyId && String(group.company_id) !== filters.companyId) return false;
    if (detailsSearch.trim() && !group.employee_name.toLowerCase().includes(detailsSearch.trim().toLowerCase())) return false;
    return true;
  });

  // The original Current Loans table, unchanged in design/columns/data -
  // shown inside the Current Loans popup, scoped to one employee's loans
  // (the popup is opened from that employee's row on Staff Loan Details).
  const renderCurrentLoansTable = (employeeId) => {
    const employeeLoans = loans.filter((loan) => loan.employee_id === employeeId);
    return loadingLoans ? (
      <div className="sl-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading staff loans...</div>
    ) : (
      <div className="sl-table-wrap">
        <table className="report-table sl-table">
          <thead>
            <tr>
              <th>Issue Date</th>
              <th>Employee Name</th>
              <th>Company</th>
              <th className="sl-num">Loan Amount</th>
              <th className="sl-num">Balance</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {employeeLoans.length === 0 ? (
              <tr><td colSpan={6} className="sl-empty">No staff loans found.</td></tr>
            ) : employeeLoans.map((loan) => {
              const settled = loan.status === 'Settled';
              return (
                <tr key={loan.id} className={settled ? 'sl-row-settled' : ''}>
                  <td>{fmtDate(loan.issued_date)}</td>
                  <td>{loan.employee_name}</td>
                  <td>{loan.company_name || '—'}</td>
                  <td className="sl-num">{fmtMoney(loan.loan_amount)}</td>
                  <td className="sl-num sl-balance">{fmtMoney(loan.balance)}</td>
                  <td>
                    <div className="sl-actions">
                      {settled ? (
                        <span className="sl-badge sl-badge-settled">Settled</span>
                      ) : canWrite && (
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => openReceiveModal(loan)}>
                          Receive Cash
                        </button>
                      )}
                      <button
                        type="button"
                        className="sl-icon-btn"
                        onClick={() => openHistory(loan)}
                        title="View repayment history"
                        aria-label={`View repayment history for ${loan.employee_name}`}
                      >
                        <FontAwesomeIcon icon={faHistory} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderStaffLoanLedgerTable = () => (
    <div className="sl-ledger-wrap">
      <table className="report-table cash-ledger-table other-ledger-table sl-ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Employee Name</th>
            <th>Description</th>
            <th className="fl-num">Debit</th>
            <th className="fl-num">Credit</th>
            <th className="fl-num">Balance</th>
          </tr>
        </thead>
        <tbody>
          {ledgerData.openingBalance !== 0 && (
            <tr>
              <td colSpan={5} style={{ fontStyle: 'italic' }}>Balance brought forward</td>
              <td className="fl-num fl-balance-cell">{fmtMoney(ledgerData.openingBalance)}</td>
            </tr>
          )}
          {loadingLoans ? (
            <tr><td colSpan={6} className="sl-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading staff loans...</td></tr>
          ) : visibleLedgerTransactions.length === 0 ? (
            <tr><td colSpan={6} className="sl-empty">
              {ledgerData.transactions.length === 0 ? 'No staff loan transactions found.' : 'No transactions match the current filters.'}
            </td></tr>
          ) : visibleLedgerTransactions.map((transaction) => (
            <tr key={transaction.key}>
              <td>{fmtDate(transaction.date)}</td>
              <td>{transaction.employee_name}</td>
              <td><span className="sl-ledger-description" title={transaction.description}>{transaction.description}</span></td>
              <td className="fl-num">{money(transaction.debit, 'fl-debit-amount')}</td>
              <td className="fl-num">{money(transaction.credit, 'fl-credit-amount')}</td>
              <td className="fl-num fl-balance-cell">{fmtMoney(transaction.balance_after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderStaffLoanDetailsTable = () => (
    <div className="sl-table-wrap">
      <table className="report-table sl-table sl-details-table">
        <thead>
          <tr>
            <th>Employee Name</th>
            <th>Company</th>
            <th className="sl-num">Total Loan Amount</th>
            <th className="sl-num">Total Amount Deducted</th>
            <th className="sl-num">Remaining Loan Balance</th>
            <th className="sl-num">Monthly Deduction Amount</th>
            <th>Last Deduction Date</th>
            <th>Loan Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loadingLoans ? (
            <tr><td colSpan={9} className="sl-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading staff loans...</td></tr>
          ) : visibleLoanDetailGroups.length === 0 ? (
            <tr><td colSpan={9} className="sl-empty">No staff loan details found.</td></tr>
          ) : visibleLoanDetailGroups.map((group) => (
            <tr key={group.key} className={group.status === 'Settled' ? 'sl-row-settled' : ''}>
              <td>{group.employee_name}</td>
              <td>{group.company_name || '—'}</td>
              <td className="sl-num">{fmtMoney(group.totalLoanAmount)}</td>
              <td className="sl-num">{fmtMoney(group.totalDeducted)}</td>
              <td className="sl-num sl-balance">{fmtMoney(group.remainingBalance)}</td>
              <td className="sl-num">{group.latestSalaryMonth ? fmtMoney(group.monthlyDeductionAmount) : '—'}</td>
              <td>{group.lastDeductionDate ? fmtDate(group.lastDeductionDate) : '—'}</td>
              <td>
                {group.status === 'Settled'
                  ? <span className="sl-badge sl-badge-settled">Settled</span>
                  : <span className="sl-badge sl-badge-active">Active</span>}
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setLoansModalEmployee({ employee_id: group.employee_id, employee_name: group.employee_name })}
                  title={`View loans for ${group.employee_name}`}
                >
                  <FontAwesomeIcon icon={faTable} /> View Loans
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="page-container sl-page cash-ledger-page">
      <header className="page-header">
        <h2 style={{ textAlign: 'left', margin: 0 }}>
          <FontAwesomeIcon icon={faHandHoldingDollar} style={{ marginRight: '8px' }} /> Staff Loans
        </h2>
        <div className="cash-ledger-header-actions">
          <label htmlFor="sl-filter-company" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', whiteSpace: 'nowrap' }}>Company:</label>
          <select
            id="sl-filter-company"
            value={filters.companyId}
            onChange={(e) => setFilters((prev) => ({ ...prev, companyId: e.target.value }))}
            className="report-filter-select"
          >
            {filterCompanies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
          </select>
          {canWrite && (
            <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <FontAwesomeIcon icon={faPlus} /> Add Staff Loan
            </button>
          )}
        </div>
      </header>

      {/* ════════════ Tabs ════════════ */}
      <div className="sl-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'ledger'}
          className={`sl-tab ${activeTab === 'ledger' ? 'active' : ''}`}
          onClick={() => setActiveTab('ledger')}
        >
          <FontAwesomeIcon icon={faBookOpen} /> Staff Loan Ledger
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'details'}
          className={`sl-tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <FontAwesomeIcon icon={faUsers} /> Staff Loan Details
        </button>
      </div>

      {activeTab === 'ledger' ? (
        <>
          {/* ════════════ Balance / Total Debit / Total Credit ════════════ */}
          <div className="cash-ledger-kpi-row">
            <div className="card cash-ledger-balance-card" style={{ display: 'flex', alignItems: 'center', padding: 'var(--spacing-lg)', gap: 'var(--spacing-xl)', marginBottom: 'var(--spacing-md)' }}>
              <div className="cash-ledger-balance-icon"><FontAwesomeIcon icon={faBookOpen} /></div>
              <div style={{ flex: 1 }}>
                <div className="cash-ledger-balance-label">
                  Staff Loan Balance{filterCompanies.find((c) => String(c.id) === filters.companyId) ? ` — ${filterCompanies.find((c) => String(c.id) === filters.companyId).name}` : ''}
                </div>
                <div className="cash-ledger-balance-value" style={{ margin: 'var(--spacing-xs) 0' }}>
                  Rs. {fmtMoney(currentBalance)}
                </div>
              </div>
            </div>
            <div className="cash-ledger-summary" style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
              <div className="card cash-ledger-total-card cash-ledger-debit-card" style={{ flex: 1, padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                <span className="cash-ledger-total-label">Total Debit</span>
                <strong className="cash-ledger-total-value">Rs. {fmtMoney(ledgerTotals.debit)}</strong>
              </div>
              <div className="card cash-ledger-total-card cash-ledger-credit-card" style={{ flex: 1, padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                <span className="cash-ledger-total-label">Total Credit</span>
                <strong className="cash-ledger-total-value">Rs. {fmtMoney(ledgerTotals.credit)}</strong>
              </div>
            </div>
          </div>

          {/* ════════════ Date + search filters ════════════ */}
          <div className="card filters-section report-filter-bar">
            <div className="ledger-filter-row">
              <input type="date" value={filters.dateFrom} onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))} title="Date From" className="report-filter-input" />
              <input type="date" value={filters.dateTo} onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))} title="Date To" className="report-filter-input" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Employee / Description"
                className="report-filter-input"
              />
            </div>
          </div>

          <div className="card cash-ledger-table-card other-ledger-table-card">
            {renderStaffLoanLedgerTable()}
          </div>
        </>
      ) : (
        <>
          {/* ════════════ Employee-wise search ════════════ */}
          <div className="card filters-section report-filter-bar">
            <div className="ledger-filter-row">
              <input
                type="text"
                value={detailsSearch}
                onChange={(e) => setDetailsSearch(e.target.value)}
                placeholder="Search employee name..."
                className="report-filter-input"
                style={{ minWidth: '240px' }}
              />
            </div>
          </div>

          <div className="card cash-ledger-table-card">
            {renderStaffLoanDetailsTable()}
          </div>
        </>
      )}

      {/* ════════════ Create Staff Loan popup ════════════ */}
      {showCreateModal && (
        <div className="sl-modal-overlay" onClick={closeCreateModal}>
          <div ref={createDialogRef} className="sl-modal" role="dialog" aria-modal="true" aria-labelledby="sl-create-title" onClick={(e) => e.stopPropagation()}>
            <div className="sl-modal-header">
              <h3 id="sl-create-title">
                <FontAwesomeIcon icon={faHandHoldingDollar} style={{ marginRight: '8px' }} />
                Create Staff Loan
              </h3>
              <button type="button" className="sl-modal-close" onClick={closeCreateModal} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleCreateLoan} noValidate>
              <div className="sl-modal-body">
                <div className="form-group">
                  <label htmlFor="sl-employee">Employee Name <span className="required">*</span></label>
                  <select id="sl-employee" name="employee_id" value={form.employee_id} onChange={handleFormChange} className="form-select" autoFocus>
                    <option value="">Select Employee</option>
                    {options.employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.employee_name}{employee.employee_code ? ` (${employee.employee_code})` : ''}
                      </option>
                    ))}
                  </select>
                  {formErrors.employee_id && <span className="form-error">{formErrors.employee_id}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="sl-company">Company <span className="required">*</span></label>
                  <select id="sl-company" name="company_id" value={form.company_id} onChange={handleFormChange} className="form-select">
                    <option value="">Select Company</option>
                    {options.companies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                  {formErrors.company_id
                    ? <span className="form-error">{formErrors.company_id}</span>
                    : <span className="form-helper">Repayments are received into this company&apos;s Cash Book.</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="sl-amount">Loan Amount (Rs.) <span className="required">*</span></label>
                  <input
                    id="sl-amount"
                    type="number"
                    name="loan_amount"
                    min="0.01"
                    step="0.01"
                    inputMode="decimal"
                    value={form.loan_amount}
                    onChange={handleFormChange}
                    placeholder="0.00"
                    className="form-input"
                  />
                  {formErrors.loan_amount && <span className="form-error">{formErrors.loan_amount}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="sl-issued">Issued Date <span className="required">*</span></label>
                  <input
                    id="sl-issued"
                    type="date"
                    name="issued_date"
                    value={form.issued_date}
                    onChange={handleFormChange}
                    className="form-input"
                  />
                  {formErrors.issued_date && <span className="form-error">{formErrors.issued_date}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="sl-cheque">Issued Cheque Details</label>
                  <textarea
                    id="sl-cheque"
                    name="cheque_details"
                    rows={2}
                    maxLength={500}
                    value={form.cheque_details}
                    onChange={handleFormChange}
                    placeholder="e.g. cheque number, bank and cheque date"
                    className="form-textarea"
                  />
                  {formErrors.cheque_details && <span className="form-error">{formErrors.cheque_details}</span>}
                </div>
              </div>

              <div className="sl-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeCreateModal} disabled={creating}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faCheck} /> Create Loan</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════ Current Loans (original table, unchanged - opened from Staff Loan Details) ════════════ */}
      {loansModalEmployee && (
        <div className="sl-modal-overlay" onClick={() => setLoansModalEmployee(null)}>
          <div ref={loansDialogRef} className="sl-modal sl-modal-wide" role="dialog" aria-modal="true" aria-labelledby="sl-loans-title" onClick={(e) => e.stopPropagation()}>
            <div className="sl-modal-header">
              <h3 id="sl-loans-title">
                <FontAwesomeIcon icon={faTable} style={{ marginRight: '8px' }} />
                Current Loans — {loansModalEmployee.employee_name}
              </h3>
              <button type="button" className="sl-modal-close" onClick={() => setLoansModalEmployee(null)} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="sl-modal-body">
              {renderCurrentLoansTable(loansModalEmployee.employee_id)}
            </div>
            <div className="sl-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setLoansModalEmployee(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════ Receive Cash Modal ════════════ */}
      {receiveLoan && (
        // Backdrop is inert: an outside click must not discard the typed amount.
        <div className="sl-modal-overlay">
          <div ref={receiveDialogRef} className="sl-modal" role="dialog" aria-modal="true" aria-labelledby="sl-receive-title">
            <div className="sl-modal-header">
              <h3 id="sl-receive-title">
                <FontAwesomeIcon icon={faHandHoldingDollar} style={{ color: '#059669', marginRight: '8px' }} />
                Receive Cash
              </h3>
              <button type="button" className="sl-modal-close" onClick={closeReceiveModal} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleReceiveCash} noValidate>
              <div className="sl-modal-body">
                <div className="sl-summary-card">
                  <div className="sl-summary-row">
                    <span className="sl-summary-lbl">Employee</span>
                    <strong>{receiveLoan.employee_name}</strong>
                  </div>
                  <div className="sl-summary-row">
                    <span className="sl-summary-lbl">Loan Amount</span>
                    <span>Rs. {fmtMoney(receiveLoan.loan_amount)}</span>
                  </div>
                  <div className="sl-summary-row">
                    <span className="sl-summary-lbl">Current Balance</span>
                    <strong className="sl-balance-strong">Rs. {fmtMoney(receiveLoan.balance)}</strong>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="sl-receive-amount">Amount (Rs.) <span className="required">*</span></label>
                  <input
                    id="sl-receive-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    inputMode="decimal"
                    value={receiveForm.amount}
                    onChange={(e) => { setReceiveForm((prev) => ({ ...prev, amount: e.target.value })); setReceiveError(''); }}
                    placeholder="0.00"
                    className="form-input"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sl-receive-date">Date <span className="required">*</span></label>
                  <input
                    id="sl-receive-date"
                    type="date"
                    min={receiveLoan.issued_date}
                    value={receiveForm.date}
                    onChange={(e) => { setReceiveForm((prev) => ({ ...prev, date: e.target.value })); setReceiveError(''); }}
                    className="form-input"
                  />
                  <span className="form-helper">The amount is added to the Cash Book on this date.</span>
                </div>

                {receiveError && <div className="sl-modal-error" role="alert">{receiveError}</div>}
              </div>

              <div className="sl-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeReceiveModal} disabled={receiving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={receiving}>
                  {receiving
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Receiving...</>
                    : <><FontAwesomeIcon icon={faCheck} /> Receive Cash</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════ Repayment History Modal ════════════ */}
      {historyLoan && (
        <div className="sl-modal-overlay" onClick={() => setHistoryLoan(null)}>
          <div ref={historyDialogRef} className="sl-modal sl-modal-wide" role="dialog" aria-modal="true" aria-labelledby="sl-history-title" onClick={(e) => e.stopPropagation()}>
            <div className="sl-modal-header">
              <h3 id="sl-history-title">
                <FontAwesomeIcon icon={faHistory} style={{ marginRight: '8px' }} />
                Repayment History
              </h3>
              <button type="button" className="sl-modal-close" onClick={() => setHistoryLoan(null)} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="sl-modal-body">
              <div className="sl-summary-card">
                <div className="sl-summary-row">
                  <span className="sl-summary-lbl">Employee</span>
                  <strong>{historyLoan.employee_name}</strong>
                </div>
                <div className="sl-summary-row">
                  <span className="sl-summary-lbl">Loan Issued</span>
                  <span>{fmtDate(historyLoan.issued_date)}</span>
                </div>
                <div className="sl-summary-row">
                  <span className="sl-summary-lbl">Loan Amount</span>
                  <span>Rs. {fmtMoney(historyLoan.loan_amount)}</span>
                </div>
                <div className="sl-summary-row">
                  <span className="sl-summary-lbl">Cheque Details</span>
                  <span className="sl-cheque-text">{historyLoan.cheque_details || '—'}</span>
                </div>
              </div>

              {loadingHistory ? (
                <div className="sl-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
              ) : (
                <div className="sl-table-wrap">
                  <table className="report-table sl-table">
                    <thead>
                      <tr>
                        <th>Date Received</th>
                        <th>Source</th>
                        <th className="sl-num">Amount</th>
                        <th className="sl-num">Balance After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.length === 0 ? (
                        <tr><td colSpan={4} className="sl-empty">No repayments received yet.</td></tr>
                      ) : history.map((repayment) => (
                        <tr key={repayment.id}>
                          <td>{fmtDate(repayment.received_date)}</td>
                          <td>
                            {repayment.source_type === 'MANUAL'
                              ? 'Receive Cash'
                              : <span className="sl-badge sl-badge-settled">
                                {repayment.source_type === 'SALARY_ENTRY' ? 'Salary Entry' : repayment.source_type === 'SALARY_ENTRY_REVERSAL' ? 'Salary Entry Reversal' : repayment.source_type}
                              </span>}
                          </td>
                          <td className="sl-num">{fmtMoney(repayment.amount)}</td>
                          <td className="sl-num">{fmtMoney(repayment.balance_after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="sl-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setHistoryLoan(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffLoans;
